import { Box, Link, Typography } from '@mui/material';
import { Fragment } from 'react';
import { S4DIssueKind, S4DToppingRef } from '../utils/s4dProductComparison';
import {
  getS4DIssueText,
  S4DIngredientLabel,
  S4DIssue,
  S4DPriceDetail,
  S4DToppingAction,
} from '../utils/s4dIssueMessages';

type Props = {
  issues: S4DIssue[];
  toppingHref?: (topping: S4DToppingRef) => string;
};

type IssuesOfKind = {
  kind: S4DIssueKind;
  title: string;
  details: IssueDetail[];
};

type IssueDetail = {
  ingredient?: S4DIngredientLabel;
  text?: string;
  prices?: S4DPriceDetail[];
  toppingAction?: S4DToppingAction;
};

const groupByKind = (issues: S4DIssue[]): IssuesOfKind[] => {
  const kinds: IssuesOfKind[] = [];
  const listedTexts = new Set<string>();

  issues.forEach((issue) => {
    const text = getS4DIssueText(issue);
    if (listedTexts.has(text)) return;
    listedTexts.add(text);

    const detail =
      issue.ingredient || issue.detail
        ? [
            {
              ingredient: issue.ingredient,
              text: issue.detail,
              prices: issue.prices,
              toppingAction: issue.toppingAction,
            },
          ]
        : [];

    const existing = kinds.find((entry) => entry.kind === issue.kind);

    if (existing) existing.details.push(...detail);
    else kinds.push({ kind: issue.kind, title: issue.title, details: detail });
  });

  return kinds;
};

export const S4DIssueList = (props: Props): JSX.Element => {
  const { issues, toppingHref } = props;

  /** `1234 - Jauheliha (duplikaatti)`: our id and note as text, the name a link to the S4D topping. */
  const renderIngredient = (ingredient: S4DIngredientLabel) => (
    <>
      {ingredient.ingredientId ? `${ingredient.ingredientId} - ` : ''}
      {ingredient.topping && toppingHref ? (
        <Link variant="body2" target="_blank" rel="noopener noreferrer" href={toppingHref(ingredient.topping)}>
          {ingredient.name}
        </Link>
      ) : (
        ingredient.name
      )}
      {ingredient.note ? ` (${ingredient.note})` : ''}
    </>
  );

  const renderToppingAction = (action: S4DToppingAction | undefined) => {
    if (!action) return null;

    return (
      <>
        {`${action.label}: `}
        {action.toppings.map((topping, index) => (
          <Fragment key={topping.topping?.s4dId ?? index}>
            {index > 0 ? ', ' : ''}
            {renderIngredient(topping)}
          </Fragment>
        ))}
      </>
    );
  };

  const renderDetail = (detail: IssueDetail) => (
    <>
      {detail.ingredient ? renderIngredient(detail.ingredient) : null}
      {detail.text ? `${detail.ingredient ? ': ' : ''}${detail.text}` : ''}
      {detail.prices?.map((price, priceIndex) => (
        <Fragment key={`${priceIndex}-${price.label}`}>
          {priceIndex === 0 ? ': ' : ', '}
          {`${price.label} `}
          <Box component="span" fontWeight={700}>
            {price.value}
          </Box>
        </Fragment>
      ))}
      {detail.toppingAction && (
        <Box component="ul" sx={{ mt: 0.25, mb: 0, pl: 2 }}>
          <Typography component="li" variant="body2" color="inherit">
            {renderToppingAction(detail.toppingAction)}
          </Typography>
        </Box>
      )}
    </>
  );

  return (
    <Box component="ul" sx={{ m: 0, pl: 2 }}>
      {groupByKind(issues).map((entry) => (
        <Typography component="li" variant="body2" color="inherit" key={entry.kind}>
          {entry.title}
          {entry.details.length > 0 && (
            <Box component="ul" sx={{ mt: 0.25, mb: 0, pl: 2 }}>
              {entry.details.map((detail, detailIndex) => (
                <Typography
                  component="li"
                  variant="body2"
                  color="inherit"
                  key={`${detailIndex}-${detail.ingredient?.ingredientId ?? detail.text ?? ''}`}
                >
                  {renderDetail(detail)}
                </Typography>
              ))}
            </Box>
          )}
        </Typography>
      ))}
    </Box>
  );
};
